import type { HydratedDocument } from 'mongoose';
import { z } from 'zod';
import { DateSchema, ObjectIdSchema } from './base';
import { StripeProducts } from './billing/StripeProducts';
import { type UserDocument, UserDocumentSchema } from './user';

export const UserCreditChargeSchema = z.object({
  id: ObjectIdSchema,
  usedQuantity: z.number().int().nonnegative(),
  timeKey: z.string(),
  updatedAt: DateSchema.optional(),
  createdAt: DateSchema.optional(),
});

export type UserCreditCharge = z.infer<typeof UserCreditChargeSchema>;

export const UserCreditChargeDocumentSchema = UserCreditChargeSchema.omit({ id: true }).extend({
  _id: ObjectIdSchema,
});

export type UserCreditChargeDocument = z.infer<typeof UserCreditChargeDocumentSchema>;

// Base schema with common fields (without id/_id field)
const UserCreditBaseSchema = z.object({
  updatedAt: DateSchema,
  createdAt: DateSchema,
  customerId: ObjectIdSchema,
  creatorId: ObjectIdSchema,
  updaterId: ObjectIdSchema,
  type: z.nativeEnum(StripeProducts),
  eventId: ObjectIdSchema.nullable(),
  expiresAt: DateSchema.nullable(),
  quantity: z.number().int().nonnegative(),
  remainingQuantity: z.number().int().nonnegative(),
});

export const UserCreditSchema = z.preprocess(
  (data: any) => {
    // Handle both id and _id fields, transform to use id
    const transformed = {
      ...data,
      id: data.id || data._id,
      charges: data.charges?.map((charge: any) => ({
        ...charge,
        id: charge.id || charge._id,
      })),
    };
    // Clean up _id if it exists
    if (data._id) {
      delete transformed._id;
    }
    return transformed;
  },
  UserCreditBaseSchema.extend({
    id: ObjectIdSchema,
    charges: z.array(UserCreditChargeSchema),
  }),
);

export type UserCredit = z.infer<typeof UserCreditSchema>;

export const EventUserCreditSchema = UserCreditBaseSchema.omit({
  'eventId': true,
}).extend({
  eventId: ObjectIdSchema,
  id: ObjectIdSchema,
  charges: z.array(UserCreditChargeSchema),
});

export type EventUserCredit = z.infer<typeof EventUserCreditSchema>;

export const UserCreditPrototypeSchema = UserCreditBaseSchema.omit({
  'createdAt': true,
  'updatedAt': true,
  'remainingQuantity': true,
  'creatorId': true,
  'updaterId': true,
}).partial({
  'eventId': true,
  'expiresAt': true,
});

export type UserCreditPrototype = z.infer<typeof UserCreditPrototypeSchema>;

export const UserCreditDocumentSchema = UserCreditBaseSchema.extend({
  _id: ObjectIdSchema,
  charges: z.array(
    UserCreditChargeSchema.omit({ id: true }).extend({
      _id: ObjectIdSchema,
    }),
  ),
  charge: z
    .function()
    .args(z.number().int().nonnegative(), z.string())
    .returns(z.promise(z.number().int().nonnegative())),
  getRemainingQuantityWithoutCurrentMonthCharges: z
    .function()
    .returns(z.number().int().nonnegative()),
  canRead: z.function().args(UserDocumentSchema.nullish()).returns(z.promise(z.boolean())),
  canEdit: z.function().args(UserDocumentSchema.nullish()).returns(z.promise(z.boolean())),
});

export interface UserCreditDocumentMethods {
  charge(args: { quantity: number; timeKey: string; persistChanges?: boolean }): Promise<number>;
  getRemainingQuantityWithoutCurrentMonthCharges(): number;
  canEdit(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  canRead(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
}

export type UserCreditDocument = HydratedDocument<
  UserCredit & { charges: Array<UserCreditChargeDocument> },
  UserCreditDocumentMethods
>;
